import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import ArrowBack from "@material-ui/icons/ArrowBack";
import Print from "@material-ui/icons/Print";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import ReceiptIcon from "@material-ui/icons/Receipt";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);

const usePageStyles = makeStyles({
  section: { marginBottom: "24px" },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "10px",
  },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "8px 24px",
  },
  label: { fontSize: "12px", color: "#888", marginBottom: "2px" },
  value: { fontSize: "14px", color: "#222", fontWeight: 500 },
  badge: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: "12px",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  th: {
    padding: "8px 10px",
    borderBottom: "2px solid #f0f0f0",
    color: "#888",
    textAlign: "left",
    fontWeight: 600,
  },
  thRight: {
    padding: "8px 10px",
    borderBottom: "2px solid #f0f0f0",
    color: "#888",
    textAlign: "right",
    fontWeight: 600,
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #f5f5f5",
    verticalAlign: "top",
  },
  tdRight: {
    padding: "8px 10px",
    borderBottom: "1px solid #f5f5f5",
    textAlign: "right",
    verticalAlign: "top",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "5px 0",
    fontSize: "14px",
  },
  summaryTotal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontSize: "16px",
    fontWeight: 700,
    borderTop: "2px solid #e0e0e0",
    marginTop: "4px",
  },
  payRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    fontSize: "13px",
    color: "#555",
  },
});

const STATUS_COLORS = {
  Completed: { bg: "#e8f5e9", color: "#2e7d32" },
  Processing: { bg: "#fff8e1", color: "#f57f17" },
  Cancelled: { bg: "#fce4ec", color: "#c62828" },
  Paid: { bg: "#e3f2fd", color: "#1565c0" },
  Shipped: { bg: "#f3e5f5", color: "#6a1b9a" },
};

const DELIVERY_STATUS_COLORS = {
  Delivered: { bg: "#e8f5e9", color: "#2e7d32" },
  Delivering: { bg: "#e3f2fd", color: "#1565c0" },
  "Not yet": { bg: "#f5f5f5", color: "#555" },
  Returned: { bg: "#fce4ec", color: "#c62828" },
};

const GRAPHQL_URL =
  (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const GET_INVOICE_QUERY = `
  query GetInvoice($id: String!) {
    getInvoice(id: $id) {
      id invoiceNumber invoiceDate returnNumber
      outlet priceBook seller saleChannel creator
      status deliveryStatus note
      shippingNumber deliveryFee carrier deliveryNote shippingNotes deliveryTime
      receiverName receiverPhone receiverAddress receiverProvince receiverWard
      weight length width height
      subtotal discount surcharges total paid cash card wallet transfer remainingCOD
      customer { id name customerNumber phone1 phone2 email address location ward }
    }
  }
`;

const GET_DETAILS_QUERY = `
  query GetInvoiceDetails($invoiceId: String!) {
    getInvoiceDetails(invoiceId: $invoiceId) {
      id productNumber barcode productName brand unit productNote
      warranty regularMaintenance
      quantity price discountPercent discount salePrice total
      product { id name images }
    }
  }
`;

const GET_SETTINGS_QUERY = `
  query GetSettings {
    getSettings {
      printInvoiceHeader
      printInvoiceFooter
    }
  }
`;

function authFetch(body) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    credentials: "include",
    body: JSON.stringify(body),
  }).then((r) => r.json());
}

function formatNum(val) {
  if (val == null || val === 0 || val === "0") return "0";
  return Number(val).toLocaleString("vi-VN");
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function Field({ label, value }) {
  const pc = usePageStyles();
  if (!value && value !== 0) return null;
  return (
    <div>
      <div className={pc.label}>{label}</div>
      <div className={pc.value}>{value}</div>
    </div>
  );
}

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const pc = usePageStyles();

  const [invoice, setInvoice] = React.useState(null);
  const [details, setDetails] = React.useState([]);
  const [settings, setSettings] = React.useState({
    printInvoiceHeader: "",
    printInvoiceFooter: "",
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      authFetch({ query: GET_INVOICE_QUERY, variables: { id } }),
      authFetch({ query: GET_DETAILS_QUERY, variables: { invoiceId: id } }),
      authFetch({ query: GET_SETTINGS_QUERY }),
    ])
      .then(([invRes, detRes, settingsRes]) => {
        if (invRes.errors) {
          setError(invRes.errors[0].message);
          return;
        }
        setInvoice(invRes.data?.getInvoice || null);
        setDetails(detRes.data?.getInvoiceDetails || []);
        if (settingsRes.data?.getSettings)
          setSettings(settingsRes.data.getSettings);
      })
      .catch(() => setError("Cannot connect to server"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <GridContainer>
        <GridItem xs={12}>
          <p style={{ textAlign: "center", padding: "40px", color: "#888" }}>
            Loading...
          </p>
        </GridItem>
      </GridContainer>
    );
  }

  if (error || !invoice) {
    return (
      <GridContainer>
        <GridItem xs={12}>
          <p style={{ textAlign: "center", padding: "40px", color: "#f44336" }}>
            {error || "Invoice not found"}
          </p>
        </GridItem>
      </GridContainer>
    );
  }

  const handlePrint = () => {
    const customerName = invoice.customer?.name || invoice.receiverName || "—";
    const phone = invoice.receiverPhone || invoice.customer?.phone1 || "—";
    const address =
      [invoice.receiverAddress, invoice.receiverProvince, invoice.receiverWard]
        .filter(Boolean)
        .join(", ") || "—";
    const note = invoice.deliveryNote || invoice.note || "";

    const printHeader = settings.printInvoiceHeader?.trim() || "";
    const printFooter = settings.printInvoiceFooter?.trim() || "";

    const rows = details
      .map(
        (d, i) => `
      <tr>
        <td>${i + 1}. ${d.productName || "—"}</td>
        <td style="text-align:center">${formatNum(d.quantity)}</td>
        <td style="text-align:right">${formatNum(d.total)}</td>
      </tr>`
      )
      .join("");

    const win = window.open("", "_blank", "width=400,height=600");
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <style>
    @page { margin: 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #000; width: 100%; }
    .print-header { text-align: center; white-space: pre-line; line-height: 1.6; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px dashed #000; }
    h2 { font-size: 13px; text-align: center; margin-bottom: 6px; }
    .info { margin-bottom: 8px; }
    .info p { margin: 2px 0; }
    .info strong { display: inline-block; min-width: 60px; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; }
    thead tr { border-bottom: 1px solid #000; }
    th { font-size: 11px; padding: 3px 2px; text-align: left; }
    th:nth-child(2) { text-align: center; }
    th:nth-child(3) { text-align: right; }
    td { padding: 3px 2px; vertical-align: top; }
    .summary { border-top: 1px solid #000; padding-top: 6px; }
    .summary-row { display: flex; justify-content: space-between; margin: 2px 0; }
    .summary-total { display: flex; justify-content: space-between; font-weight: bold; font-size: 13px; margin-top: 4px; border-top: 1px solid #000; padding-top: 4px; }
    .print-footer { text-align: center; white-space: pre-line; line-height: 1.6; margin-top: 12px; padding-top: 8px; border-top: 1px dashed #000; font-size: 10px; color: #444; }
  </style>
</head>
<body>
  ${printHeader ? `<div class="print-header">${printHeader}</div>` : ""}
  <div class="info">
    <p><strong>Customer:</strong> ${customerName}</p>
    <p><strong>Phone:</strong> ${phone}</p>
    <p><strong>Address:</strong> ${address}</p>
    ${note ? `<p><strong>Note:</strong> ${note}</p>` : ""}
  </div>
  <table>
    <thead>
      <tr>
        <th>Product</th>
        <th style="text-align:center">Qty</th>
        <th style="text-align:right">Total</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="summary">
    <div class="summary-row"><span>Subtotal</span><span>${formatNum(
      invoice.subtotal
    )}</span></div>
    ${
      invoice.deliveryFee > 0
        ? `<div class="summary-row"><span>Delivery fee</span><span>${formatNum(
            invoice.deliveryFee
          )}</span></div>`
        : ""
    }
    <div class="summary-total"><span>Total</span><span>${formatNum(
      invoice.total
    )}</span></div>
  </div>
  ${printFooter ? `<div class="print-footer">${printFooter}</div>` : ""}
  <script>window.onload = function(){ window.print(); window.close(); }<\/script>
</body>
</html>`);
    win.document.close();
  };

  const sc = STATUS_COLORS[invoice.status] || { bg: "#f5f5f5", color: "#555" };
  const dc = DELIVERY_STATUS_COLORS[invoice.deliveryStatus] || {
    bg: "#f5f5f5",
    color: "#555",
  };
  const debt = (invoice.total || 0) - (invoice.paid || 0);
  const hasShipping =
    invoice.shippingNumber || invoice.carrier || invoice.receiverName;
  const hasPayment =
    (invoice.cash || 0) +
      (invoice.card || 0) +
      (invoice.wallet || 0) +
      (invoice.transfer || 0) >
    0;

  return (
    <GridContainer>
      <GridItem xs={12}>
        {/* Back / Print buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <Button
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => history.goBack()}
            style={{ color: "#555" }}
          >
            Back
          </Button>
          <Button
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            style={{ color: "#1976d2" }}
          >
            Print
          </Button>
        </div>

        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <ReceiptIcon />
            </CardIcon>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                paddingTop: "8px",
              }}
            >
              <h4 className={classes.cardIconTitle} style={{ margin: 0 }}>
                {invoice.invoiceNumber}
              </h4>
              <span
                className={pc.badge}
                style={{ backgroundColor: sc.bg, color: sc.color }}
              >
                {invoice.status || "—"}
              </span>
              {invoice.deliveryStatus && (
                <span
                  className={pc.badge}
                  style={{ backgroundColor: dc.bg, color: dc.color }}
                >
                  {invoice.deliveryStatus}
                </span>
              )}
            </div>
          </CardHeader>

          <CardBody>
            {/* ── General Info ── */}
            <div className={pc.section}>
              <div className={pc.sectionTitle}>General</div>
              <div className={pc.grid3}>
                <Field
                  label="Invoice date"
                  value={formatDateTime(invoice.invoiceDate)}
                />
                <Field label="Outlet" value={invoice.outlet} />
                <Field label="Price book" value={invoice.priceBook} />
                <Field label="Seller" value={invoice.seller} />
                <Field label="Creator" value={invoice.creator} />
                <Field label="Sale channel" value={invoice.saleChannel} />
                {invoice.returnNumber && (
                  <Field label="Return number" value={invoice.returnNumber} />
                )}
              </div>
              {invoice.note && (
                <div style={{ marginTop: "10px" }}>
                  <div className={pc.label}>Note</div>
                  <div className={pc.value} style={{ whiteSpace: "pre-wrap" }}>
                    {invoice.note}
                  </div>
                </div>
              )}
            </div>

            <Divider style={{ margin: "0 0 20px" }} />

            {/* ── Customer ── */}
            {invoice.customer && (
              <>
                <div className={pc.section}>
                  <div className={pc.sectionTitle}>Customer</div>
                  <div className={pc.grid3}>
                    <Field
                      label="Customer number"
                      value={invoice.customer.customerNumber}
                    />
                    <Field label="Name" value={invoice.customer.name} />
                    <Field
                      label="Phone"
                      value={invoice.customer.phone1 || invoice.customer.phone2}
                    />
                    <Field label="Email" value={invoice.customer.email} />
                    <Field
                      label="Address"
                      value={[
                        invoice.customer.address,
                        invoice.customer.location,
                        invoice.customer.ward,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    />
                  </div>
                </div>
                <Divider style={{ margin: "0 0 20px" }} />
              </>
            )}

            {/* ── Shipping ── */}
            {hasShipping && (
              <>
                <div className={pc.section}>
                  <div className={pc.sectionTitle}>Delivery</div>
                  <div className={pc.grid3}>
                    <Field
                      label="Shipping number"
                      value={invoice.shippingNumber}
                    />
                    <Field label="Carrier" value={invoice.carrier} />
                    <Field
                      label="Delivery fee"
                      value={
                        invoice.deliveryFee
                          ? formatNum(invoice.deliveryFee)
                          : null
                      }
                    />
                    <Field label="Receiver" value={invoice.receiverName} />
                    <Field
                      label="Receiver phone"
                      value={invoice.receiverPhone}
                    />
                    <Field
                      label="Delivery time"
                      value={formatDateTime(invoice.deliveryTime)}
                    />
                    <Field
                      label="Address"
                      value={[
                        invoice.receiverAddress,
                        invoice.receiverProvince,
                        invoice.receiverWard,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    />
                    {(invoice.weight ||
                      invoice.length ||
                      invoice.width ||
                      invoice.height) && (
                      <Field
                        label="Size (W×L×H cm / g)"
                        value={`${invoice.weight || 0}g · ${
                          invoice.length || 0
                        }×${invoice.width || 0}×${invoice.height || 0} cm`}
                      />
                    )}
                  </div>
                  {invoice.deliveryNote && (
                    <div style={{ marginTop: "10px" }}>
                      <div className={pc.label}>Delivery note</div>
                      <div className={pc.value}>{invoice.deliveryNote}</div>
                    </div>
                  )}
                  {invoice.shippingNotes && (
                    <div style={{ marginTop: "10px" }}>
                      <div className={pc.label}>Shipping notes</div>
                      <div className={pc.value}>{invoice.shippingNotes}</div>
                    </div>
                  )}
                </div>
                <Divider style={{ margin: "0 0 20px" }} />
              </>
            )}

            {/* ── Product lines ── */}
            <div className={pc.section}>
              <div className={pc.sectionTitle}>
                Products ({details.length} item{details.length !== 1 ? "s" : ""}
                )
              </div>
              <table className={pc.table}>
                <thead>
                  <tr>
                    <th className={pc.th}>#</th>
                    <th className={pc.th}>Product</th>
                    <th className={pc.th}>Code</th>
                    <th className={pc.thRight}>Price</th>
                    <th className={pc.thRight}>Qty</th>
                    <th className={pc.thRight}>Discount</th>
                    <th className={pc.thRight}>Sale price</th>
                    <th className={pc.thRight}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {details.map((d, i) => (
                    <tr key={d.id}>
                      <td
                        className={pc.td}
                        style={{ color: "#888", width: "30px" }}
                      >
                        {i + 1}
                      </td>
                      <td className={pc.td}>
                        <div style={{ fontWeight: 600 }}>{d.productName}</div>
                        {d.brand && (
                          <div style={{ fontSize: "12px", color: "#888" }}>
                            {d.brand}
                          </div>
                        )}
                        {d.unit && (
                          <div style={{ fontSize: "12px", color: "#aaa" }}>
                            Unit: {d.unit}
                          </div>
                        )}
                        {d.productNote && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              fontStyle: "italic",
                            }}
                          >
                            {d.productNote}
                          </div>
                        )}
                        {d.warranty && (
                          <div style={{ fontSize: "12px", color: "#1976d2" }}>
                            Warranty: {d.warranty}
                          </div>
                        )}
                      </td>
                      <td
                        className={pc.td}
                        style={{ color: "#555", fontSize: "12px" }}
                      >
                        {d.productNumber || "—"}
                      </td>
                      <td className={pc.tdRight}>{formatNum(d.price)}</td>
                      <td className={pc.tdRight}>{formatNum(d.quantity)}</td>
                      <td
                        className={pc.tdRight}
                        style={{ color: d.discount > 0 ? "#e53935" : "#999" }}
                      >
                        {d.discountPercent > 0
                          ? `${d.discountPercent}%`
                          : d.discount > 0
                          ? formatNum(d.discount)
                          : "—"}
                      </td>
                      <td className={pc.tdRight}>{formatNum(d.salePrice)}</td>
                      <td className={pc.tdRight} style={{ fontWeight: 600 }}>
                        {formatNum(d.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Divider style={{ margin: "0 0 20px" }} />

            {/* ── Financials ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "24px",
              }}
            >
              {/* Payment methods */}
              <div>
                <div className={pc.sectionTitle}>Payment</div>
                {hasPayment ? (
                  <>
                    {invoice.cash > 0 && (
                      <div className={pc.payRow}>
                        <span>Cash</span>
                        <span>{formatNum(invoice.cash)}</span>
                      </div>
                    )}
                    {invoice.card > 0 && (
                      <div className={pc.payRow}>
                        <span>Card</span>
                        <span>{formatNum(invoice.card)}</span>
                      </div>
                    )}
                    {invoice.wallet > 0 && (
                      <div className={pc.payRow}>
                        <span>Wallet</span>
                        <span>{formatNum(invoice.wallet)}</span>
                      </div>
                    )}
                    {invoice.transfer > 0 && (
                      <div className={pc.payRow}>
                        <span>Transfer</span>
                        <span>{formatNum(invoice.transfer)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: "#aaa", fontSize: "13px" }}>
                    No payment recorded
                  </div>
                )}
                {invoice.remainingCOD > 0 && (
                  <div
                    className={pc.payRow}
                    style={{ color: "#e53935", marginTop: "4px" }}
                  >
                    <span>Remaining COD</span>
                    <span>{formatNum(invoice.remainingCOD)}</span>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div>
                <div className={pc.sectionTitle}>Summary</div>
                <div className={pc.summaryRow}>
                  <span style={{ color: "#555" }}>Subtotal</span>
                  <span>{formatNum(invoice.subtotal)}</span>
                </div>
                {invoice.discount > 0 && (
                  <div className={pc.summaryRow}>
                    <span style={{ color: "#e53935" }}>Discount</span>
                    <span style={{ color: "#e53935" }}>
                      −{formatNum(invoice.discount)}
                    </span>
                  </div>
                )}
                {invoice.surcharges > 0 && (
                  <div className={pc.summaryRow}>
                    <span style={{ color: "#555" }}>Surcharges</span>
                    <span>{formatNum(invoice.surcharges)}</span>
                  </div>
                )}
                {invoice.deliveryFee > 0 && (
                  <div className={pc.summaryRow}>
                    <span style={{ color: "#555" }}>Delivery fee</span>
                    <span>{formatNum(invoice.deliveryFee)}</span>
                  </div>
                )}
                <div className={pc.summaryTotal}>
                  <span>Total</span>
                  <span>{formatNum(invoice.total)}</span>
                </div>
                <div className={pc.summaryRow}>
                  <span style={{ color: "#555" }}>Paid</span>
                  <span style={{ color: "#2e7d32", fontWeight: 600 }}>
                    {formatNum(invoice.paid)}
                  </span>
                </div>
                {debt > 0 && (
                  <div className={pc.summaryRow}>
                    <span style={{ color: "#e53935", fontWeight: 600 }}>
                      Outstanding
                    </span>
                    <span style={{ color: "#e53935", fontWeight: 600 }}>
                      {formatNum(debt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
